import { KeyboardArrowDown } from '@mui/icons-material'
import { Button, Menu, MenuItem } from '@mui/material'
import { useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'

import { SelectOption } from 'components/Select'
import { useAuthUser } from 'features/user'
import useUpdateCurrentWorkspace from 'features/workspace/hooks/useUpdateCurrentWorkspace'
import { useMenu } from 'hooks/useMenu'
import { ROUTES } from 'routes'
import { Option } from './Option'
import { WorkspaceSlice } from './types'
import { initialOptions, toOptionProps, toSelectOptions } from './utils'

export const WorkspaceSelect = (): React.JSX.Element => {
  const {
    user: { currentWorkspace, id: userId, workspaces },
  } = useAuthUser()
  const navigate = useNavigate()

  const workspacesOptions = toSelectOptions(workspaces)
  const { update } = useUpdateCurrentWorkspace({ userId })

  const { anchor, close, isOpen, open } = useMenu()
  const [selectedWorkspace, setSelectedWorkspace] = useState<SelectOption<WorkspaceSlice>>(
    initialOptions(workspacesOptions, currentWorkspace?.id),
  )

  const handleUpdate = async (option: SelectOption<WorkspaceSlice>): Promise<void> => {
    const { value } = option

    const status = await update(value)
    if (status === 'failure') return

    setSelectedWorkspace(option)
    navigate(generatePath(ROUTES.PRODUCT.LIST, { workspaceId: value }), { replace: true })
    close()
  }

  return (
    <>
      <Button
        data-cy='workspace-select-menu'
        endIcon={<KeyboardArrowDown data-cy='keyboard-arrow-down-icon' />}
        onClick={open}
      >
        <Option {...toOptionProps(selectedWorkspace)} />
      </Button>

      <Menu
        anchorEl={anchor}
        onClose={close}
        open={isOpen}
      >
        {workspacesOptions.map(
          (option: SelectOption<WorkspaceSlice>): React.JSX.Element => (
            <MenuItem
              data-cy={option.label}
              key={option.value}
              onClick={() => void handleUpdate(option)}
              selected={option.value === selectedWorkspace.value}
            >
              <Option {...toOptionProps(option)} />
            </MenuItem>
          ),
        )}
      </Menu>
    </>
  )
}
